import Table from "common/src/components/widget/Table";
import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Currency from "common/src/components/widget/Currency";
import {serviceType} from "../../model/serviceType";
import {toEuroCurrency} from "common/src/utils/CurrencyUtils";
import {toUnit} from "common/src/utils/NumberUtils";
import {footprintUnit} from "../../model/footprintUnit";

function CostBreakdownCard(props) {
  const {
    getField,
    setValue,
    values
  } = props.form;

  const {t} = useTranslation();
  const serviceTypes = useMemo(() => {
    return serviceType.map(function(type){
      return {
        ...type,
        name: t(type.name, {count: 3})
      }
    })
  }, [])

  const [costBreakdown, setCostBreakdown] = useState(serviceTypes);
  const [costBreakdownTotal, setCostBreakdownTotal] = useState([]);
  const [priceToPay, setPriceToPay] = useState('0');

  useEffect(() => {
    if(!values.days) {
      return;
    }
    let totalReservationsPrice = 0;

    if (values.trailServicePrice === undefined) {
      totalReservationsPrice += values.calculatedTrailServicePrice;
    } else {
      totalReservationsPrice += values.trailServicePrice;
    }

    const properties = {price: 0, carbonFootprint: 0, ecologicalFootprint: 0, wasteFootprint: 0, waterFootprint: 0}
    let serviceProperties = {
      accommodation: {...properties},
      transport: {...properties},
      activity: {...properties},
      bike_rental: {...properties}
    }
    let tableFooter = {
      name: t('offer.total'),
      ...properties
    };
    values.days.forEach(day => {
      day.services.forEach(service => {
        if(!service.type || service.service == null) {
          return;
        }
        if(isFinite(service.price)) {
          totalReservationsPrice += +service.price;
          serviceProperties[service.type].price += +service.price;
        }

        const serviceFootprint = service.service.footprint;

        serviceProperties[service.type].carbonFootprint += serviceFootprint.carbonFootprint;
        tableFooter.carbonFootprint += serviceFootprint.carbonFootprint;
        serviceProperties[service.type].ecologicalFootprint += serviceFootprint.ecologicalFootprint;
        tableFooter.ecologicalFootprint += serviceFootprint.ecologicalFootprint;
        serviceProperties[service.type].wasteFootprint += serviceFootprint.wasteFootprint;
        tableFooter.wasteFootprint += serviceFootprint.wasteFootprint;
        serviceProperties[service.type].waterFootprint += serviceFootprint.waterFootprint;
        tableFooter.waterFootprint += serviceFootprint.waterFootprint;
      })
    })
    tableFooter.price = toEuroCurrency(totalReservationsPrice);
    tableFooter.carbonFootprint = toUnit(tableFooter.carbonFootprint, footprintUnit.carbon, 5);
    tableFooter.ecologicalFootprint = toUnit(tableFooter.ecologicalFootprint, footprintUnit.ecological, 5);
    tableFooter.wasteFootprint = toUnit(tableFooter.wasteFootprint, footprintUnit.waste, 5);
    tableFooter.waterFootprint = toUnit(tableFooter.waterFootprint, footprintUnit.water, 5);
    setCostBreakdownTotal([tableFooter]);

    let tableData = [...costBreakdown];
    tableData[0].properties = serviceProperties.accommodation;
    tableData[1].properties = serviceProperties.bike_rental;
    tableData[2].properties = serviceProperties.transport;
    tableData[3].properties = serviceProperties.activity;
    tableData[4] = {id: "trail-service", name: "Trail service", properties: {price: values.trailServicePrice !== undefined ? values.trailServicePrice : values.calculatedTrailServicePrice}}
    setCostBreakdown(tableData);

    let toPay = (totalReservationsPrice ?? 0) - (values.amountPaid ?? 0);
    toPay = toPay > 0 ? toPay : 0;
    setPriceToPay(toPay.toFixed(2));
    setValue("price", totalReservationsPrice, undefined, null);
  }, [values.days, values.days?.length, values.amountPaid, values.numberOfAdults, values.numberOfChildren, values.trailServicePrice, values.calculatedTrailServicePrice])

  return (
    <div className="card">
      <div className="card-header">
        <p className="card-header-title">
          <span className="icon fz fz-money-check mr-1"/>
          <span>{t("offer.cost_breakdown")}</span>
        </p>
      </div>
      <div className="card-content">
        <Table
          data={costBreakdown}
          footer={costBreakdownTotal}
          columns={[
            {name: t("offer.service_type"), id: 'name'},
            {name: t("offer.price"), id: 'price', cellComponent: (service) => <div>{toEuroCurrency(service.properties?.price)}</div>},
            {name: t("footprint.carbon_footprint"), id: 'carbonFootprint', cellComponent: (service) => <div>{toUnit(service.properties?.carbonFootprint, footprintUnit.carbon, 5)}</div>},
            {name: t("footprint.ecological_footprint"), id: 'ecologicalFootprint', cellComponent: (service) => <div>{toUnit(service.properties?.ecologicalFootprint, footprintUnit.ecological, 5)}</div>},
            {name: t("footprint.waste_generation"), id: 'wasteFootprint', cellComponent: (service) => <div>{toUnit(service.properties?.wasteFootprint, footprintUnit.waste, 5)}</div>},
            {name: t("footprint.water_consumption"), id: 'waterFootprint', cellComponent: (service) => <div>{toUnit(service.properties?.waterFootprint, footprintUnit.water, 5)}</div>}]}
        />
        <div className="columns mt-4">
          <div className="column is-narrow mt-4"><p className="has-text-weight-bold	">{t("offer.amount_paid")}</p></div>
          <div className="column is-3">
            <Currency label={t("offer.amount_paid_by_guest")}
                   formField={getField("amountPaid")}/>
          </div>
          <div className="column is-3">
            <Currency label={t("offer.left_to_pay")}
                   disabled={true}
                   value={priceToPay}/>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CostBreakdownCard;
